import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Spinner } from '#pplx/frontend/aether/components/Spinner/index.js';
import { useUpsellActions } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useUpsellActions.js';
import { LearnMoreButton } from '#pplx/frontend/apps/ask/upsells/components/LearnMoreButton.js';
import Button from '#pplx/frontend/components/buttons/Button.js';
import TextButton from '#pplx/frontend/components/buttons/TextButton.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
export const UpsellButton = React.memo(function UpsellButton({ upsellInformation, inFlight, position, onButtonClick, isLoading, textButtonIfSecondary = false, hideUpsell, verticalLayout = false, }) {
    const handleUpsell = useUpsellActions({
        upsellInformation,
        inFlight,
        options: { hideUpsell },
    });
    // Local pending flag so the primary button can show a spinner while the
    // CTA handler runs. Required for handlers that await network work — e.g.
    // handleRunInComputer awaits the /attachments/duplicate endpoint before
    // submitting the new Computer task; without this, the user clicks and sees
    // nothing until the navigation fires.
    const [isClickPending, setIsClickPending] = useState(false);
    const isPrimaryButtonLoading = Boolean(isLoading || isClickPending);
    const handleClick = useCallback(async () => {
        if (isPrimaryButtonLoading)
            return;
        onButtonClick?.();
        setIsClickPending(true);
        try {
            await handleUpsell();
        }
        finally {
            setIsClickPending(false);
        }
    }, [isPrimaryButtonLoading, onButtonClick, handleUpsell]);
    // Get variant from button color
    const getButtonVariant = useCallback((buttonColor) => {
        if (position === 'router_steps') {
            return 'inverted';
        }
        switch (buttonColor) {
            case 'MAX_COLOR':
                return 'maxGold';
            case 'PRIMARY_COLOR':
                return 'primary';
            case 'INVERTED':
                return 'inverted';
            default:
                return 'common';
        }
    }, [position]);
    const buttonVariant = useMemo(() => getButtonVariant(upsellInformation?.button_color), [getButtonVariant, upsellInformation?.button_color]);
    // Check for new multi-CTA system (secondary_button/tertiary_button)
    const secondaryButton = upsellInformation?.cta_information?.secondary_button;
    const tertiaryButton = upsellInformation?.cta_information?.tertiary_button;
    // Create handlers for secondary and tertiary buttons
    // Use useMemo to create modified upsell information for secondary/tertiary buttons
    const secondaryUpsellInfo = useMemo(() => {
        if (!secondaryButton)
            return upsellInformation;
        return {
            ...upsellInformation,
            cta: secondaryButton.cta,
            button_text: secondaryButton.button_text,
        };
    }, [upsellInformation, secondaryButton]);
    const tertiaryUpsellInfo = useMemo(() => {
        if (!tertiaryButton)
            return upsellInformation;
        return {
            ...upsellInformation,
            cta: tertiaryButton.cta,
            button_text: tertiaryButton.button_text,
        };
    }, [upsellInformation, tertiaryButton]);
    const secondaryButtonHandler = useUpsellActions({
        upsellInformation: secondaryUpsellInfo,
        inFlight,
        options: { hideUpsell },
    });
    const tertiaryButtonHandler = useUpsellActions({
        upsellInformation: tertiaryUpsellInfo,
        inFlight,
        options: { hideUpsell },
    });
    // Render an action button (secondary/tertiary)
    const renderActionButton = useCallback((actionButton, handler) => {
        const variant = getButtonVariant(actionButton.button_color);
        const isSecondaryColor = actionButton.button_color === 'SECONDARY_COLOR';
        const handleActionClick = () => {
            onButtonClick?.();
            handler();
        };
        const buttonProps = {
            size: 'regular',
            onClick: handleActionClick,
            text: actionButton.button_text,
            textClassName: '!text-sm',
            fullWidth: true,
        };
        // Use TextButton for secondary color if requested
        if (textButtonIfSecondary && isSecondaryColor) {
            return _jsx(TextButton, { ...buttonProps, variant: "super", noPadding: true });
        }
        return _jsx(Button, { ...buttonProps, variant: variant });
    }, [getButtonVariant, textButtonIfSecondary, onButtonClick]);
    const primaryUpsellButton = useCallback((largeButton) => {
        let icon = undefined;
        if (upsellInformation?.icon_reference === 'comet_download') {
            icon = _jsx(Icon, { icon: $icon('download'), size: "small" });
        }
        let trailing = undefined;
        if (upsellInformation?.cta === 'RUN_IN_COMPUTER') {
            trailing = (_jsx("span", { className: "inline-flex items-center opacity-60", children: _jsx(Icon, { icon: $icon('arrow-back'), size: "small" }) }));
        }
        const buttonProps = {
            size: largeButton ? 'regular' : 'small',
            onClick: handleClick,
            text: upsellInformation?.button_text,
            leadingComponent: isPrimaryButtonLoading ? _jsx(Spinner, { size: largeButton ? 16 : 14 }) : icon,
            trailingComponent: trailing,
            textClassName: largeButton ? '!text-sm' : undefined,
            fullWidth: true,
            disabled: isPrimaryButtonLoading,
        };
        if (textButtonIfSecondary && upsellInformation?.button_color === 'SECONDARY_COLOR') {
            // side card secondary buttons are TextButtons
            return _jsx(TextButton, { ...buttonProps, variant: "super", noPadding: true });
        }
        return _jsx(Button, { ...buttonProps, variant: buttonVariant });
    }, [
        buttonVariant,
        handleClick,
        upsellInformation?.button_color,
        upsellInformation?.button_text,
        upsellInformation?.cta,
        upsellInformation?.icon_reference,
        textButtonIfSecondary,
        isPrimaryButtonLoading,
    ]);
    const hasNewButtonSystem = secondaryButton || tertiaryButton;
    // Legacy support for deprecated secondary_permalink
    const hasLegacySecondaryPermalink = !hasNewButtonSystem && upsellInformation?.cta_information?.secondary_permalink;
    if (hasNewButtonSystem) {
        // New multi-CTA system
        const buttons = [
            primaryUpsellButton(true),
            secondaryButton && renderActionButton(secondaryButton, secondaryButtonHandler),
            tertiaryButton && renderActionButton(tertiaryButton, tertiaryButtonHandler),
        ].filter(Boolean);
        if (buttons.length === 1) {
            return buttons[0];
        }
        return (_jsx("div", { className: classNames('gap-4 flex', verticalLayout ? 'flex-col' : 'flex-row'), children: verticalLayout ? buttons : [...buttons].reverse() }));
    }
    if (hasLegacySecondaryPermalink) {
        // Legacy permalink system (deprecated)
        const secondaryUrl = upsellInformation?.cta_information?.secondary_permalink;
        const secondaryNewTab = upsellInformation?.cta_information?.secondary_permalink_new_tab;
        const legacySecondaryButton = (_jsx(LearnMoreButton, { url: secondaryUrl, variant: "text", openInNewTab: secondaryNewTab, upsellInformation: upsellInformation }));
        return (_jsx("div", { className: classNames('gap-4 flex', verticalLayout ? 'flex-col' : 'flex-row'), children: verticalLayout ? (_jsxs(_Fragment, { children: [primaryUpsellButton(true), legacySecondaryButton] })) : (_jsxs(_Fragment, { children: [legacySecondaryButton, primaryUpsellButton(true)] })) }));
    }
    return primaryUpsellButton(false);
});
//# sourceMappingURL=UpsellButton.js.map